import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Chip } from '@mui/material';
import { Chart, LineController, LineElement, PointElement, CategoryScale, LinearScale, Title, Tooltip, Filler } from 'chart.js';

Chart.register(LineController, LineElement, PointElement, CategoryScale, LinearScale, Title, Tooltip, Filler);

const PriceChart = () => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const { currentPrice, pricePrediction, hasPaidForPrediction } = useSelector((state) => state.aiTrading);

  const getTrend = () => {
    if (!currentPrice || !pricePrediction) return null;
    return pricePrediction.predictedPrice > currentPrice.price ? { label: 'Up Trend (Buy)', color: '#2ecc71' } : { label: 'Down Trend (Sell)', color: '#e74c3c' };
  };

  useEffect(() => {
    if (hasPaidForPrediction && chartRef.current && currentPrice && pricePrediction) {
      const ctx = chartRef.current.getContext('2d');
      const gradient = ctx.createLinearGradient(0, 0, 0, 300);
      gradient.addColorStop(0, 'rgba(52, 152, 219, 0.4)');
      gradient.addColorStop(1, 'rgba(52, 152, 219, 0)');

      if (!chartInstance.current) {
        chartInstance.current = new Chart(ctx, {
          type: 'line',
          data: {
            labels: ['Current', 'Predicted'],
            datasets: [
              {
                label: 'BTC/USDT Price',
                data: [currentPrice.price, pricePrediction.predictedPrice],
                borderColor: '#3498db',
                backgroundColor: gradient,
                fill: true,
                tension: 0.4,
                pointRadius: 5,
                pointHoverRadius: 8,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: false,
                title: { display: true, text: 'Price (USDT)', color: '#1a1a1a' },
                grid: { color: 'rgba(0,0,0,0.1)' },
                ticks: { color: '#1a1a1a' },
              },
              x: {
                grid: { display: false },
                ticks: { color: '#1a1a1a' },
              },
            },
            plugins: {
              tooltip: {
                backgroundColor: '#fff',
                titleColor: '#1a1a1a',
                bodyColor: '#1a1a1a',
                borderColor: '#3498db',
                borderWidth: 1,
              },
            },
          },
        });
      } else {
        chartInstance.current.data.datasets[0].data = [currentPrice.price, pricePrediction.predictedPrice];
        chartInstance.current.update();
      }
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [hasPaidForPrediction]);

  useEffect(() => {
    if (chartInstance.current && currentPrice && pricePrediction) {
      chartInstance.current.data.datasets[0].data = [currentPrice.price, pricePrediction.predictedPrice];
      chartInstance.current.update();
    }
  }, [currentPrice, pricePrediction]);

  if (!hasPaidForPrediction) {
    return null;
  }

  const trend = getTrend();

  return (
    <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', height: { xs: '300px', md: '400px' } }}>
      {(!currentPrice || !pricePrediction) ? (
        <Typography variant="body2" align="center" color="text.secondary">
          Waiting for price data...
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#1a1a1a' }}>
              BTC/USDT Price Chart
            </Typography>
            {trend && (
              <Chip
                label={trend.label}
                sx={{ bgcolor: trend.color, color: '#fff', fontWeight: 'bold' }}
              />
            )}
          </Box>
          <canvas ref={chartRef} style={{ width: '100%', height: '100%' }} />
        </>
      )}
    </Box>
  );
};

export default PriceChart;